/**
 * MATLAB Lexer
 * Tokenizes MATLAB/Octave source code into a stream of tokens
 */
import { MatlabTokenType, MatlabLexerError } from "./types";
const KEYWORDS = {
    function: MatlabTokenType.FUNCTION,
    end: MatlabTokenType.END,
    if: MatlabTokenType.IF,
    elseif: MatlabTokenType.ELSEIF,
    else: MatlabTokenType.ELSE,
    for: MatlabTokenType.FOR,
    while: MatlabTokenType.WHILE,
    switch: MatlabTokenType.SWITCH,
    case: MatlabTokenType.CASE,
    otherwise: MatlabTokenType.OTHERWISE,
    try: MatlabTokenType.TRY,
    catch: MatlabTokenType.CATCH,
    return: MatlabTokenType.RETURN,
    break: MatlabTokenType.BREAK,
    continue: MatlabTokenType.CONTINUE,
    global: MatlabTokenType.GLOBAL,
    persistent: MatlabTokenType.PERSISTENT,
    classdef: MatlabTokenType.CLASSDEF,
};
export class MatlabLexer {
    input;
    pos = 0;
    line = 1;
    column = 1;
    tokens = [];
    constructor(input) {
        this.input = input;
    }
    tokenize() {
        while (this.pos < this.input.length) {
            this.skipWhitespace();
            if (this.pos >= this.input.length)
                break;
            const char = this.current();
            // Skip comments
            if (char === "%") {
                this.skipComment();
                continue;
            }
            // Line continuation
            if (char === "." && this.peek(1) === "." && this.peek(2) === ".") {
                this.skipLineContinuation();
                continue;
            }
            // Newline
            if (char === "\n") {
                this.tokens.push(this.makeToken(MatlabTokenType.NEWLINE, "\n"));
                this.advance();
                this.line++;
                this.column = 1;
                continue;
            }
            // Numbers
            if (this.isDigit(char) || (char === "." && this.isDigit(this.peek(1)))) {
                this.readNumber();
                continue;
            }
            // Identifiers and keywords
            if (this.isAlpha(char) || char === "_") {
                this.readIdentifier();
                continue;
            }
            // Strings (both ' and " are supported)
            if (char === "'" && this.shouldStartString()) {
                this.readString("'");
                continue;
            }
            if (char === '"') {
                this.readString('"');
                continue;
            }
            // Operators and delimiters
            this.readOperator();
        }
        this.tokens.push(this.makeToken(MatlabTokenType.EOF, ""));
        return this.tokens;
    }
    current() {
        return this.input[this.pos] || "";
    }
    peek(offset = 1) {
        return this.input[this.pos + offset] || "";
    }
    advance() {
        const char = this.current();
        this.pos++;
        this.column++;
        return char;
    }
    makeToken(type, value) {
        return {
            type,
            value,
            line: this.line,
            column: this.column - String(value).length,
        };
    }
    skipWhitespace() {
        while (this.pos < this.input.length) {
            const char = this.current();
            if (char === " " || char === "\t" || char === "\r") {
                this.advance();
                continue;
            }
            break;
        }
    }
    skipComment() {
        // Check for block comment %{ ... %}
        if (this.current() === "%" && this.peek(1) === "{") {
            this.advance(); // %
            this.advance(); // {
            while (this.pos < this.input.length) {
                if (this.current() === "%" && this.peek(1) === "}") {
                    this.advance(); // %
                    this.advance(); // }
                    break;
                }
                if (this.current() === "\n") {
                    this.line++;
                    this.column = 0;
                }
                this.advance();
            }
        }
        else {
            // Single line comment
            while (this.pos < this.input.length && this.current() !== "\n") {
                this.advance();
            }
        }
    }
    skipLineContinuation() {
        // Skip ...
        this.advance();
        this.advance();
        this.advance();
        // Skip rest of line
        while (this.pos < this.input.length && this.current() !== "\n") {
            this.advance();
        }
        // Skip newline
        if (this.current() === "\n") {
            this.advance();
            this.line++;
            this.column = 1;
        }
    }
    isDigit(char) {
        return char >= "0" && char <= "9";
    }
    isAlpha(char) {
        return (char >= "a" && char <= "z") || (char >= "A" && char <= "Z");
    }
    isAlphanumeric(char) {
        return this.isDigit(char) || this.isAlpha(char) || char === "_";
    }
    // Determine if ' should start a string or be transpose
    shouldStartString() {
        // If no previous token, it's a string
        if (this.tokens.length === 0)
            return true;
        const lastToken = this.tokens[this.tokens.length - 1];
        // After these tokens, ' is transpose
        const transposeAfter = [
            MatlabTokenType.RPAREN,
            MatlabTokenType.RBRACKET,
            MatlabTokenType.RBRACE,
            MatlabTokenType.IDENTIFIER,
            MatlabTokenType.NUMBER,
            MatlabTokenType.TRANSPOSE,
            MatlabTokenType.CTRANSPOSE,
        ];
        return !transposeAfter.includes(lastToken.type);
    }
    readNumber() {
        const startColumn = this.column;
        let numStr = "";
        // Read integer part
        while (this.isDigit(this.current())) {
            numStr += this.advance();
        }
        // Decimal point
        if (this.current() === "." &&
            this.peek(1) !== "." &&
            this.peek(1) !== "*" &&
            this.peek(1) !== "/" &&
            this.peek(1) !== "^" &&
            this.peek(1) !== "\\" &&
            this.peek(1) !== "'") {
            numStr += this.advance(); // consume '.'
            while (this.isDigit(this.current())) {
                numStr += this.advance();
            }
        }
        // Exponent
        if (this.current() === "e" || this.current() === "E") {
            numStr += this.advance();
            if (this.current() === "+" || this.current() === "-") {
                numStr += this.advance();
            }
            if (!this.isDigit(this.current())) {
                throw new MatlabLexerError("Invalid number: expected digit after exponent", {
                    line: this.line,
                    column: this.column,
                });
            }
            while (this.isDigit(this.current())) {
                numStr += this.advance();
            }
        }
        // Check for imaginary unit (i or j in MATLAB)
        if ((this.current() === "i" || this.current() === "j") &&
            !this.isAlphanumeric(this.peek(1))) {
            this.advance(); // consume 'i' or 'j'
            this.tokens.push({
                type: MatlabTokenType.IMAGINARY,
                value: parseFloat(numStr),
                line: this.line,
                column: startColumn,
            });
        }
        else {
            this.tokens.push({
                type: MatlabTokenType.NUMBER,
                value: parseFloat(numStr),
                line: this.line,
                column: startColumn,
            });
        }
    }
    readIdentifier() {
        const startColumn = this.column;
        let name = "";
        while (this.isAlphanumeric(this.current())) {
            name += this.advance();
        }
        // Note: 'i' and 'j' as standalone identifiers should be parsed as IDENTIFIER
        // The evaluator will treat them as imaginary if not overwritten
        // Only treat them as imaginary when directly following a number (like 3i, 2j)
        // That case is handled in readNumber
        // Check for keywords
        const keyword = KEYWORDS[name.toLowerCase()];
        if (keyword) {
            this.tokens.push({
                type: keyword,
                value: name,
                line: this.line,
                column: startColumn,
            });
        }
        else {
            this.tokens.push({
                type: MatlabTokenType.IDENTIFIER,
                value: name,
                line: this.line,
                column: startColumn,
            });
        }
    }
    readString(quote) {
        const startColumn = this.column;
        this.advance(); // consume opening quote
        let str = "";
        while (this.pos < this.input.length && this.current() !== quote) {
            if (this.current() === "\n") {
                throw new MatlabLexerError("Unterminated string literal", {
                    line: this.line,
                    column: startColumn,
                });
            }
            // MATLAB escape: double quote for literal quote
            if (this.current() === quote && this.peek(1) === quote) {
                str += quote;
                this.advance();
                this.advance();
            }
            else {
                str += this.advance();
            }
        }
        if (this.current() !== quote) {
            throw new MatlabLexerError("Unterminated string literal", {
                line: this.line,
                column: startColumn,
            });
        }
        this.advance(); // consume closing quote
        this.tokens.push({
            type: MatlabTokenType.STRING,
            value: str,
            line: this.line,
            column: startColumn,
            quote,
        });
    }
    readOperator() {
        const char = this.current();
        const next = this.peek(1);
        // Three-character operators
        if (char === "." && next === "." && this.peek(2) === ".") {
            this.tokens.push(this.makeToken(MatlabTokenType.ELLIPSIS, "..."));
            this.advance();
            this.advance();
            this.advance();
            return;
        }
        // Two-character operators
        if (char === "=" && next === "=") {
            this.tokens.push(this.makeToken(MatlabTokenType.EQ, "=="));
            this.advance();
            this.advance();
            return;
        }
        if (char === "~" && next === "=") {
            this.tokens.push(this.makeToken(MatlabTokenType.NE, "~="));
            this.advance();
            this.advance();
            return;
        }
        if (char === "<" && next === "=") {
            this.tokens.push(this.makeToken(MatlabTokenType.LE, "<="));
            this.advance();
            this.advance();
            return;
        }
        if (char === ">" && next === "=") {
            this.tokens.push(this.makeToken(MatlabTokenType.GE, ">="));
            this.advance();
            this.advance();
            return;
        }
        if (char === "&" && next === "&") {
            this.tokens.push(this.makeToken(MatlabTokenType.SHORT_AND, "&&"));
            this.advance();
            this.advance();
            return;
        }
        if (char === "|" && next === "|") {
            this.tokens.push(this.makeToken(MatlabTokenType.SHORT_OR, "||"));
            this.advance();
            this.advance();
            return;
        }
        if (char === "." && next === "*") {
            this.tokens.push(this.makeToken(MatlabTokenType.DOT_MULTIPLY, ".*"));
            this.advance();
            this.advance();
            return;
        }
        if (char === "." && next === "/") {
            this.tokens.push(this.makeToken(MatlabTokenType.DOT_DIVIDE, "./"));
            this.advance();
            this.advance();
            return;
        }
        if (char === "." && next === "\\") {
            this.tokens.push(this.makeToken(MatlabTokenType.DOT_BACKSLASH, ".\\"));
            this.advance();
            this.advance();
            return;
        }
        if (char === "." && next === "^") {
            this.tokens.push(this.makeToken(MatlabTokenType.DOT_POWER, ".^"));
            this.advance();
            this.advance();
            return;
        }
        if (char === "." && next === "'") {
            this.tokens.push(this.makeToken(MatlabTokenType.CTRANSPOSE, ".'"));
            this.advance();
            this.advance();
            return;
        }
        // Single-character operators
        const singleOps = {
            "+": MatlabTokenType.PLUS,
            "-": MatlabTokenType.MINUS,
            "*": MatlabTokenType.MULTIPLY,
            "/": MatlabTokenType.DIVIDE,
            "\\": MatlabTokenType.BACKSLASH,
            "^": MatlabTokenType.POWER,
            "=": MatlabTokenType.ASSIGN,
            "<": MatlabTokenType.LT,
            ">": MatlabTokenType.GT,
            "&": MatlabTokenType.AND,
            "|": MatlabTokenType.OR,
            "~": MatlabTokenType.NOT,
            "(": MatlabTokenType.LPAREN,
            ")": MatlabTokenType.RPAREN,
            "[": MatlabTokenType.LBRACKET,
            "]": MatlabTokenType.RBRACKET,
            "{": MatlabTokenType.LBRACE,
            "}": MatlabTokenType.RBRACE,
            ",": MatlabTokenType.COMMA,
            ":": MatlabTokenType.COLON,
            ";": MatlabTokenType.SEMICOLON,
            "@": MatlabTokenType.AT,
            ".": MatlabTokenType.DOT,
        };
        // Handle transpose after checking for .operations
        if (char === "'" && !this.shouldStartString()) {
            this.tokens.push(this.makeToken(MatlabTokenType.TRANSPOSE, "'"));
            this.advance();
            return;
        }
        const tokenType = singleOps[char];
        if (tokenType) {
            this.tokens.push(this.makeToken(tokenType, char));
            this.advance();
            return;
        }
        throw new MatlabLexerError(`Unexpected character: ${char}`, {
            line: this.line,
            column: this.column,
        });
    }
}
/**
 * Convenience function to tokenize MATLAB code
 */
export function matlabTokenize(input) {
    const lexer = new MatlabLexer(input);
    return lexer.tokenize();
}
//# sourceMappingURL=lexer.js.map